import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { convertProsemirrorToMarkdown } from '@atlassian/ai-model-io/convert-prosemirror-to-markdown';

const GENERATE_MARKER = '<!-- GENERATE_HERE -->';

/**
 * Serialises the entire editor document to Markdown, injecting a
 * `<!-- GENERATE_HERE -->` marker at the cursor position.
 * Used for the manual Cmd+Shift+Space trigger.
 *
 * Instead of serializing the full document and then trying to locate the
 * cursor in the markdown text, we split the ProseMirror document at the
 * cursor position using `doc.slice()` and serialize each half independently.
 * This follows the same pattern used by `editor-plugin-ai` for RovoChat
 * (see `request-options-rovo-agent.ts` and `request-options-convo-ai.ts`).
 */
export function serializeDocWithCursorMarker(view: EditorView, cursorPos: number): string {
	const doc = view.state.doc;

	// Serialize content before the cursor
	const beforeSlice = doc.slice(0, cursorPos);
	const markdownPlusSanitizeProseMirrorOnlyNodes = fg(
		'platform_editor_markdown_plus_sanitize_pm_nodes',
	);
	const { markdown: mdBefore } = convertProsemirrorToMarkdown({
		editorView: view,
		fragment: beforeSlice.content,
		featureToggles: { markdownPlusSanitizeProseMirrorOnlyNodes },
	});

	// Serialize content after the cursor
	const afterSlice = doc.slice(cursorPos);
	const { markdown: mdAfter } = convertProsemirrorToMarkdown({
		editorView: view,
		fragment: afterSlice.content,
		featureToggles: { markdownPlusSanitizeProseMirrorOnlyNodes },
	});

	const before = mdBefore.trimEnd();
	const after = mdAfter.trimStart();

	if (after) {
		return `${before} ${GENERATE_MARKER}\n${after}`;
	}
	return `${before} ${GENERATE_MARKER}`;
}
